<script setup lang="ts">
    import { onMounted, reactive } from 'vue';
    import { bsAlert, AlertTypes } from '@shared/components/BsAlerts';
    import { getGlobalTemplates, getTemplate, saveTemplate, GlobalTemplateRef, GlobalTemplate } from './GlobalTemplates.access';

    const state = reactive<{
        templates: GlobalTemplateRef[];
        loading: boolean;
        selectedTemplate: GlobalTemplate | null;
        saving: boolean;
    }>({
        templates: [],
        loading: false,
        selectedTemplate: null,
        saving: false,
    });

    onMounted(async () => {
        state.loading = true;
        try {
            state.templates = await getGlobalTemplates();
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: 'Error', content: 'Failed to load templates' });
        } finally {
            state.loading = false;
        }
    });

    async function openTemplate(ref: GlobalTemplateRef): Promise<void> {
        try {
            state.selectedTemplate = await getTemplate(ref.TemplateId);
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: 'Error', content: 'Failed to load template' });
        }
    }

    async function save(): Promise<void> {
        if (!state.selectedTemplate) return;
        state.saving = true;
        try {
            await saveTemplate(state.selectedTemplate);
            bsAlert({ type: AlertTypes.Success, title: 'Successful', content: 'Template saved' });
            state.selectedTemplate = null;
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: 'Some troubles', content: 'Check this with the nice guys at dev' });
        } finally {
            state.saving = false;
        }
    }
</script>

<template>
    <h1 class="main-title">Global templates</h1>
    <h2 class="main-sub-title">Edit html and css</h2>

    <div v-if="state.loading">Loading...</div>
    <ul v-else class="row d-flex flex-wrap list-unstyled">
        <li v-for="template in state.templates" :key="template.TemplateId" class="flex-col col-lg-3 col-md-4 col-sm-6 col-xs-6">
            <div class="panel panel-default" style="cursor: pointer" @click="openTemplate(template)">
                <img class="img-responsive" :src="template.ImageUrl" :alt="template.Name" />
                <div class="panel-body">
                    <h4>{{ template.Name }}</h4>
                    <p>{{ template.Description }}</p>
                </div>
            </div>
        </li>
    </ul>

    <Teleport to="body">
        <div v-if="state.selectedTemplate" class="modal fade in" style="display: block" tabindex="-1" role="dialog">
            <div class="modal-dialog" style="width: 90%">
                <div class="modal-content">
                    <div class="modal-header">
                        <button type="button" class="close" @click="state.selectedTemplate = null">&times;</button>
                        <h4 class="modal-title">Edit global template: {{ state.selectedTemplate.Name }}</h4>
                    </div>
                    <div class="modal-body">
                        <h4>HTML</h4>
                        <textarea v-model="state.selectedTemplate.EditableHtml" class="form-control" rows="10"></textarea>
                        <h4>CSS</h4>
                        <textarea v-model="state.selectedTemplate.EditableCss" class="form-control" rows="10"></textarea>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-link" @click="state.selectedTemplate = null">Close</button>
                        <button type="button" class="btn btn-primary" :disabled="state.saving" @click="save">Save changes</button>
                    </div>
                </div>
            </div>
        </div>
        <div v-if="state.selectedTemplate" class="modal-backdrop fade in"></div>
    </Teleport>
</template>

<script lang="ts">
    export default {
        name: 'GlobalTemplates',
    };
</script>

<style></style>
